import React, { useState } from 'react';
import { X, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { Input } from './Input';
import { Logo } from './Logo';
import { login, signup, validatePassword } from '../utils/auth';
interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onForgotPassword: () => void;
  onSignupSuccess: (email: string) => void;
  initialMode?: 'login' | 'signup';
  context?: string; // Why they need to sign in
}
export function AuthModal({
  isOpen,
  onClose,
  onSuccess,
  onForgotPassword,
  onSignupSuccess,
  initialMode = 'login',
  context
}: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Login form
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  // Signup form
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupUsername, setSignupUsername] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);
  const passwordValidation = validatePassword(signupPassword);
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await login(loginUsername, loginPassword);
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0]);
      return;
    }
    setIsLoading(true);
    try {
      const result = await signup(signupUsername, signupEmail, signupPassword, signupName);
      if (result.success) {
        if (result.verificationRequired) {
          onSignupSuccess(signupEmail);
          onClose();
        } else {
          onSuccess();
          onClose();
        }
      } else {
        setError(result.error || 'Signup failed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  const resetForm = () => {
    setLoginUsername('');
    setLoginPassword('');
    setSignupName('');
    setSignupEmail('');
    setSignupUsername('');
    setSignupPassword('');
    setError('');
    setShowPassword(false);
  };
  const switchMode = () => {
    resetForm();
    setMode(mode === 'login' ? 'signup' : 'login');
  };
  return <AnimatePresence data-id="element-64">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" data-id="element-65" />

          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[90vh] overflow-y-auto" data-id="element-66">
            <div className="sticky top-0 bg-white border-b border-sand/50 px-6 py-4 flex items-center justify-between z-10" data-id="element-67">
              <Logo variant="icon" size="sm" data-id="element-68" />
              <button onClick={onClose} className="p-2 hover:bg-sand/30 rounded-full transition-colors" data-id="element-69">
                <X size={24} className="text-charcoal" data-id="element-70" />
              </button>
            </div>

            <div className="p-6 pb-8" data-id="element-71">
              {/* Context Message */}
              {context && <div className="mb-6 p-4 bg-sky-blue/10 border border-sky-blue/30 rounded-ios-lg" data-id="element-72">
                  <p className="text-sm text-charcoal text-center" data-id="element-73">{context}</p>
                </div>}

              {/* Mode Toggle */}
              <div className="flex gap-2 mb-6 p-1 bg-sand/30 rounded-ios" data-id="element-74">
                <button onClick={() => mode !== 'login' && switchMode()} className={`flex-1 py-3 rounded-ios font-semibold transition-all ${mode === 'login' ? 'bg-white text-dark-green shadow-sm' : 'text-moss-gray'}`} data-id="element-75">
                  Log In
                </button>
                <button onClick={() => mode !== 'signup' && switchMode()} className={`flex-1 py-3 rounded-ios font-semibold transition-all ${mode === 'signup' ? 'bg-white text-dark-green shadow-sm' : 'text-moss-gray'}`} data-id="element-76">
                  Sign Up
                </button>
              </div>

              {/* Error Message */}
              {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-4 p-3 bg-red-50 border border-red-200 rounded-ios flex items-start gap-2" data-id="element-77">
                  <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-78" />
                  <p className="text-sm text-red-600" data-id="element-79">{error}</p>
                </motion.div>}

              {/* Login Form */}
              {mode === 'login' && <form onSubmit={handleLogin} className="space-y-4" data-id="element-80">
                  <Input label="Username" type="text" value={loginUsername} onChange={setLoginUsername} placeholder="Enter your username" data-id="element-81" />

                  <div className="relative" data-id="element-82">
                    <Input label="Password" type={showPassword ? 'text' : 'password'} value={loginPassword} onChange={setLoginPassword} placeholder="Enter your password" data-id="element-83" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-[38px] text-moss-gray hover:text-charcoal" data-id="element-84">
                      {showPassword ? <EyeOff size={20} data-id="element-85" /> : <Eye size={20} data-id="element-86" />}
                    </button>
                  </div>

                  <div className="flex justify-end" data-id="element-87">
                    <button type="button" onClick={onForgotPassword} className="text-sm text-dark-green font-medium hover:underline" data-id="element-88">
                      Forgot Password?
                    </button>
                  </div>

                  <Button type="submit" variant="primary" fullWidth size="lg" loading={isLoading} disabled={!loginUsername || !loginPassword || isLoading} className="mt-2" data-id="element-89">
                    Log In
                  </Button>

                  <div className="mt-4 p-4 bg-sand/20 rounded-ios" data-id="element-90">
                    <p className="text-xs text-charcoal/70 mb-2 font-medium" data-id="element-91">
                      Demo Accounts:
                    </p>
                    <p className="text-xs text-charcoal/70" data-id="element-92">
                      <strong data-id="element-93">User 1:</strong> jimmy / jimmyallhere
                    </p>
                    <p className="text-xs text-charcoal/70" data-id="element-94">
                      <strong data-id="element-95">User 2:</strong> bri / briallhere
                    </p>
                  </div>
                </form>}

              {/* Signup Form */}
              {mode === 'signup' && <form onSubmit={handleSignup} className="space-y-4" data-id="element-96">
                  <Input label="Full Name" type="text" value={signupName} onChange={setSignupName} placeholder="Enter your full name" data-id="element-97" />

                  <Input label="Email" type="email" value={signupEmail} onChange={setSignupEmail} placeholder="Enter your email" data-id="element-98" />

                  <Input label="Username" type="text" value={signupUsername} onChange={setSignupUsername} placeholder="Choose a username" data-id="element-99" />

                  <div className="relative" data-id="element-100">
                    <Input label="Password" type={showPassword ? 'text' : 'password'} value={signupPassword} onChange={setSignupPassword} placeholder="Create a strong password" onFocus={() => setPasswordFocused(true)} onBlur={() => setPasswordFocused(false)} data-id="element-101" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-[38px] text-moss-gray hover:text-charcoal" data-id="element-102">
                      {showPassword ? <EyeOff size={20} data-id="element-103" /> : <Eye size={20} data-id="element-104" />}
                    </button>
                  </div>

                  {/* Password Requirements */}
                  {(passwordFocused || signupPassword) && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="p-4 bg-sand/20 rounded-ios space-y-2" data-id="element-105">
                      <p className="text-xs font-semibold text-charcoal mb-2" data-id="element-106">
                        Password Requirements:
                      </p>
                      <div className="space-y-1" data-id="element-107">
                        <PasswordRequirement met={signupPassword.length >= 8} text="At least 8 characters" data-id="element-108" />
                        <PasswordRequirement met={/[A-Z]/.test(signupPassword)} text="One uppercase letter" data-id="element-109" />
                        <PasswordRequirement met={/[a-z]/.test(signupPassword)} text="One lowercase letter" data-id="element-110" />
                        <PasswordRequirement met={/[0-9]/.test(signupPassword)} text="One number" data-id="element-111" />
                      </div>
                      {signupPassword && <div className="pt-2 mt-2 border-t border-sand/50" data-id="element-112">
                          <p className="text-xs text-charcoal/70" data-id="element-113">
                            Strength:{' '}
                            <span className={`font-semibold ${passwordValidation.strength === 'strong' ? 'text-green-600' : passwordValidation.strength === 'medium' ? 'text-yellow-600' : 'text-red-600'}`} data-id="element-114">
                              {passwordValidation.strength.charAt(0).toUpperCase() + passwordValidation.strength.slice(1)}
                            </span>
                          </p>
                        </div>}
                    </motion.div>}

                  <Button type="submit" variant="primary" fullWidth size="lg" loading={isLoading} disabled={!signupName || !signupEmail || !signupUsername || !passwordValidation.isValid || isLoading} className="mt-6" data-id="element-115">
                    Create Account
                  </Button>
                </form>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
function PasswordRequirement({
  met,
  text
}: {
  met: boolean;
  text: string;
}) {
  return <div className="flex items-center gap-2" data-id="element-116">
      {met ? <CheckCircle size={14} className="text-green-600" data-id="element-117" /> : <div className="w-3.5 h-3.5 rounded-full border-2 border-moss-gray" data-id="element-118" />}
      <span className={`text-xs ${met ? 'text-green-600' : 'text-charcoal/70'}`} data-id="element-119">
        {text}
      </span>
    </div>;
}